import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  VStack,
  Heading,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Flex,
  Container,
} from '@chakra-ui/react';
import { LOGO_URL, PAGE_TITLES } from '../constants/branding';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8889';
  const verificationAttempted = React.useRef(false);

  // Set document title
  useEffect(() => {
    document.title = PAGE_TITLES.VERIFY_EMAIL;
  }, []);

  useEffect(() => {
    const verifyEmail = async () => {
      // Prevent duplicate verification attempts
      if (verificationAttempted.current) {
        return;
      }
      verificationAttempted.current = true;

      console.log('Token from URL:', token);
      
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link');
        return;
      }

      try {
        const response = await fetch(`${baseUrl}/auth/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token: token }),
        });

        const data = await response.json();
        console.log('Verification response:', data);

        if (response.ok) {
          setStatus('success');
          setMessage('Email verified successfully!');
          setTimeout(() => navigate('/login'), 3000);
        } else {
          setStatus('error');
          setMessage(data.detail || 'Verification failed');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage('An error occurred during verification');
      }
    };

    verifyEmail();
  }, [token, navigate, baseUrl]);

  console.log('Current status:', status);
  console.log('Current message:', message);

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg="gray.50"
      position="relative"
      overflow="hidden"
    >
      {/* Background Design */}
      <Box
        position="absolute"
        top="-10%"
        right="-10%"
        width="600px"
        height="600px"
        bg="blue.500"
        borderRadius="full"
        opacity={0.1}
      />
      <Box
        position="absolute"
        bottom="-15%"
        left="-15%"
        width="800px"
        height="800px"
        bg="blue.400"
        borderRadius="full"
        opacity={0.1}
      />

      <Container maxW="sm" position="relative" zIndex={1}>
        <VStack 
          spacing={6} 
          p={8} 
          textAlign="center" 
          bg="white" 
          borderRadius="xl" 
          boxShadow="lg"
          width="100%"
        >
          <Box mb={1}>
            <img 
              src={LOGO_URL}
              alt="IT360 Logo" 
              style={{ height: '50px', width: 'auto' }} 
            />
          </Box>
          <Heading size="lg" color="blue.600">Email Verification</Heading>
          
          {status === 'loading' && (
            <VStack spacing={3}>
              <Spinner size="lg" color="blue.500" thickness="3px" />
              <Text fontSize="sm">Verifying your email...</Text>
            </VStack>
          )}

          {status === 'success' && (
            <Alert status="success" borderRadius="md" fontSize="sm">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <Text>{message}</Text>
                <Text fontSize="xs">Redirecting to login page...</Text>
              </VStack>
            </Alert>
          )}

          {status === 'error' && (
            <Alert status="error" borderRadius="md" fontSize="sm">
              <AlertIcon />
              <VStack align="start" spacing={3}>
                <Text>{message}</Text>
                <Button size="sm" fontSize="xs" onClick={() => navigate('/login')}>
                  Return to Login
                </Button>
              </VStack>
            </Alert>
          )}
        </VStack>
      </Container>
    </Flex>
  );
};

export default VerifyEmail; 